// src/common/firebase/FirestoreService.ts
import { addDoc, collection, doc, getDoc, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import Copyright from "../../model/Copyright";
import ContactPerson, { ContactWorkType } from "../../model/customer/ContactPerson";
import CustomerInquiry from "../../model/customer/CustomerInquiry";
import CustomerReport from "../../model/customer/CustomerReport";
import Marketing from "../../model/Marketing";
import Notice from "../../model/Notice";
import Patent from "../../model/Patent";
import Recruit from "../../model/Recruit";
import TabManager from "../../model/TabManager";
import { firestore } from "./Firebase";

/**
 * 🔍 특허 및 인증 목록 조회
 * @async
 * @function fetchPatents
 * @param {string[]} types - 특허 종류 (TabManager를 통해 종류 조회 가능. screenName: patent)
 * @returns Promise<Patent[]>
 */
const fetchPatents = async (types: string[]): Promise<Patent[]> => {
    const patentRef = collection(firestore, "patent");
    const q = query(patentRef, where("patentType", "in", types), orderBy("title", "asc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
            id: doc.id,
            patentType: data.patentType,
            title: data.title,
            englishTitle: data.englishTitle,
            chineseTitle: data.chineseTitle,
            representImage: data.representImage,
            acquisitionDate: data.acquisitionDate?.toDate(),
            isPublished: data.isPublished,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    });
};

/**
 * 🔍 채용공고 목록 조회
 * @async
 * @function fetchRecruits
 * @param {string[]} types - 채용공고 종류 (TabManager를 통해 종류 조회 가능. screenName: recruit)
 * @returns Promise<Recruit[]>
 */
const fetchRecruits = async (types: string[]): Promise<Recruit[]> => {
    const recruitRef = collection(firestore, "recruit");
    const q = query(recruitRef, where("jobType", "in", types), where("isPublished", "==", true), orderBy("jobTitle", "asc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
                id: doc.id,
                jobType: data.jobType,
                jobTitle: data.jobTitle,
                contents: data.contents,
                recruitStartDate: data.recruitStartDate.toDate(),
                recruitEndDate: data.recruitEndDate?.toDate(),
                isPublished: data.isPublished,
                attachedFiles: data.attachedFiles,
                createdBy: data.createdBy,
                createdAt: data.createdAt.toDate(),
                updatedBy: data.updatedBy,
                updatedAt: data.updatedAt.toDate(),
            };
    })
};

/**
 * 🔍 채용공고 단일 조회
 * @async
 * @function fetchRecruit
 * @param {string} id - 채용공고 docId
 * @returns Promise<Recruit | null>
 */
const fetchRecruit = async (id: string): Promise<Recruit | null> => {
    const docRef = doc(firestore, "recruit", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            jobType: data.jobType,
            jobTitle: data.jobTitle,
            contents: data.contents,
            recruitStartDate: data.recruitStartDate.toDate(),
            recruitEndDate: data.recruitEndDate?.toDate(),
            isPublished: data.isPublished,
            attachedFiles: data.attachedFiles,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    } else {
        console.warn("📭 Recruit 문서를 찾을 수 없습니다.");
        return null;
    }
};

/**
 * 🔍 홍보자료 목록 조회
 * @async
 * @function fetchMarketings
 * @param {string[]} types - 홍보자료 종류 (TabManager를 통해 종류 조회 가능. screenName: marketing)
 * @returns Promise<Recruit | null>
 */
const fetchMarketings = async (types: string[]): Promise<Marketing[]> => {
    const marketingRef = collection(firestore, "marketing");
    const q = query(marketingRef, where("marketingType", "in", types), where("isPublished", "==", true), orderBy("postDate", "desc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
            id: doc.id,
            marketingType: data.marketingType,
            title: data.title,
            representImage: data.representImage,
            contents: data.contents,
            attachedFiles: data.attachedFiles || [],
            postDate: data.postDate?.toDate() || null,
            isPublished: data.isPublished,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    })
};

/**
 * 🔍 홍보자료 목록 조회
 * @async
 * @function fetchLatestMediaCenterMarketings
 * @param {string[]} types - 홍보자료 종류 (TabManager를 통해 종류 조회 가능. screenName: marketing)
 * @returns Promise<Recruit | null>
 */
const fetchLatestMediaCenterMarketings = async (types: string[]): Promise<Marketing[]> => {
    const marketingRef = collection(firestore, "marketing");
    const q = query(marketingRef, where("marketingType", "in", types), where("isPublished", "==", true), orderBy("postDate", "desc"), limit(3));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
            id: doc.id,
            marketingType: data.marketingType,
            title: data.title,
            representImage: data.representImage,
            contents: data.contents,
            attachedFiles: data.attachedFiles || [],
            postDate: data.postDate?.toDate() || null,
            isPublished: data.isPublished,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    })
};

/**
 * 🔍 홍보자료 단일 조회
 * @async
 * @function fetchMarketing
 * @param {string} id - 홍보자료 docId
 * @returns Promise<Marketing | null>
 */
const fetchMarketing = async (id: string): Promise<Marketing | null> => {
    const docRef = doc(firestore, "marketing", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            marketingType: data.marketingType,
            title: data.title,
            representImage: data.representImage,
            contents: data.contents,
            attachedFiles: data.attachedFiles || [],
            postDate: data.postDate?.toDate() || null,
            isPublished: data.isPublished,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    } else {
        console.warn("📭 Marketing 문서를 찾을 수 없습니다.");
        return null;
    }
};

/**
 * 🔍 세부 타입 조회 함수
 * @async
 * @function fetchTabManagers
 * @param {string} screenName - 화면 종류 {recruit: 채용, patent: 특허 및 인증, marketing: 홍보}
 * @returns Promise<TabManager[]>
 */
const fetchTabManagers = async (screenName: string): Promise<TabManager[]> => {
    const tabRef = collection(firestore, "tabManager");
    const q = query(tabRef, where("screenName", "==", screenName), orderBy("tabOrder", "asc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
        id: doc.id,
        screenName: data.screenName,
        tabCode: data.tabCode,
        tabName: data.tabName,
        createdBy: data.createdBy,
        createdAt: data.createdAt?.toDate(),
        updatedBy: data.updatedBy,
        updatedAt: data.updatedAt?.toDate(),
        };
    });
};

/**
 * 🔍 고객 신고 생성 함수
 * @async
 * @function createCustomerReport
 * @param {CustomerReport} data - 신고 데이터
 * @returns Promise<void>
 */
const createCustomerReport = async (data: CustomerReport): Promise<void> => {
    try {
        const customerReportRef = collection(firestore, "customerReport");

        const payload = {
            requestorName: data.requestorName,
            dateOfBirth: data.dateOfBirth,
            mobileNo: data.mobileNo,
            email: data.email,
            title: data.title,
            contents: data.contents,
            collectInfoAgreeYn: data.collectInfoAgreeYn,
            replyCompletedYn: data.replyCompletedYn,
            createdAt: data.createdAt
        };

        await addDoc(customerReportRef, payload);
        //console.log("✅ 새 신고건이 성공적으로 추가되었습니다.");
    } catch (error) {
        console.error("❌ 신고건 추가 중 오류 발생:", error);
        throw error;
    }
};

/**
 * 🔍 고객 문의 생성 함수
 * @async
 * @function createCustomerInquiry
 * @param {CustomerInquiry} data - 문의 데이터
 * @returns Promise<void>
 */
const createCustomerInquiry = async (data: CustomerInquiry): Promise<void> => {
    try {
        const inquiryRef = collection(firestore, "customerInquiry");

        const payload = {
            requestorName: data.requestorName,
            email: data.email,
            title: data.title,
            contents: data.contents,
            replyCompletedYn: data.replyCompletedYn,
            createdAt: data.createdAt
        };

        await addDoc(inquiryRef, payload);
        //console.log("✅ 새 문의건이 성공적으로 추가되었습니다.");
    } catch (error) {
        console.error("❌ 문의건 추가 중 오류 발생:", error);
        throw error;
    }
};

/**
 * 🔍 담당자 조회 함수
 * @async
 * @function fetchContactPerson
 * @param {ContactWorkType} worktype - 담당자 업무 타입
 * @returns Promise<ContactPerson>
 */
const fetchContactPerson = async (worktype: ContactWorkType): Promise<ContactPerson> => {
    const contactpersonRef = collection(firestore, 'contactperson');
    const q = query(contactpersonRef, where('worktype', '==', worktype));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
        throw new Error(`No ContactPerson found for worktype: ${worktype}`);
    }

    const doc = querySnapshot.docs[0];
    const data = doc.data();

    return {
        id: doc.id,
        worktype: contactWorkTypeFrom(data.worktype),
        workname: data.workname,
        name: data.name,
        email: data.email,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
    };
};

/**
 * 🔍 저작권 조회 함수
 * @async
 * @function fetchCopyright
 * @returns Promise<Copyright>
 */
const fetchCopyright = async (): Promise<Copyright> => {
    const copyrightRef = collection(firestore, "copyright");
    const querySnapshot = await getDocs(copyrightRef);

    if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();

        return {
            companyName: data.companyName ?? '',
            address: data.address ?? '',
            corpRegNo: data.corpRegNo ?? '',
            representPersonName: data.representPersonName ?? '',
            copyright: data.copyright ?? '',
        };
    }

    // 문서가 없을 경우 기본값 반환
    return {
        companyName: '',
        address: '',
        corpRegNo: '',
        representPersonName: '',
        copyright: '',
    };
};

/**
 * 🔍 공지사항 목록 조회 함수
 * @async
 * @function fetchNotices
 * @returns Promise<Notice[]>
 */
const fetchNotices = async (): Promise<Notice[]> => {
    const noticeRef = collection(firestore, "notice");
    const q = query(noticeRef, where("isPublished", "==", true), orderBy("postDate", "desc"), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
            id: doc.id,
            title: data.title,
            contents: data.contents,
            attachedFiles: data.attachedFiles || [],
            postDate: data.postDate.toDate(),
            isPublished: data.isPublished,
            isAnchored: data.isAnchored,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    });
};

/**
 * 🔍 단일 공지사항 조회 함수
 * @async
 * @function fetchNotice
 * @param {string} id - Doc ID
 * @returns Promise<Notice | null>
 */
const fetchNotice = async (id: string): Promise<Notice | null> => {
    const docRef = doc(firestore, "notice", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        const data = docSnap.data();
        return {
            id: docSnap.id,
            title: data.title,
            contents: data.contents,
            attachedFiles: data.attachedFiles || [],
            postDate: data.postDate.toDate(),
            isPublished: data.isPublished,
            isAnchored: data.isAnchored,
            createdBy: data.createdBy,
            createdAt: data.createdAt.toDate(),
            updatedBy: data.updatedBy,
            updatedAt: data.updatedAt.toDate(),
        };
    } else {
        console.warn("📭 Notice 문서를 찾을 수 없습니다.");
        return null;
    }
};

/**
 * 🔍 담당자 업무 타입 조회 함수
 * @async
 * @function contactWorkTypeFrom
 * @param {string} value - 문의 데이터
 * @returns ContactWorkType
 */
function contactWorkTypeFrom(value: string): ContactWorkType {
    switch (value) {
    case "inquiry":
    return ContactWorkType.Inquiry;
    case "report":
    return ContactWorkType.Report;
    default:
    throw new Error(`Unknown worktype value: ${value}`);
    }
}

export { createCustomerInquiry, createCustomerReport, fetchContactPerson, fetchCopyright, fetchLatestMediaCenterMarketings, fetchMarketing, fetchMarketings, fetchNotice, fetchNotices, fetchPatents, fetchRecruit, fetchRecruits, fetchTabManagers };
